import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  IAssignedAdminRoles,
  IPaginatedResponse,
  IRole, RoleAuthTarget,
} from '@boma/client-shared';
import { APP_CONFIG } from '@boma/app-config';

export interface ICreateOrUpdateEntityRoleRequest {
  name: string;
  entityId: string;
  description: string;
  permissions: string[];
  target: RoleAuthTarget;
}

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  listRoles(
    entityId: string = '',
    page: string = '1',
    perPage: string = '25',
    query: string = '',
    target: string = '0'
  ) {
    return this.httpClient.get<IPaginatedResponse<IRole>>(
      `${this.base}api/v${this.version}/${entityId}/Roles`,
      {
        params: { page, query, perPage, target },
      }
    );
  }

  loadEntityRoleDetails(roleId: string, entityId: string){
    return this.httpClient.get<IRole>(
      `${this.base}api/v${this.version}/${entityId}/Roles/${roleId}`,
    );
  }

  getTargetPermissions(target: string) {
    return this.httpClient.get<string[]>(
      `${this.base}api/v${this.version}/Permissions/${target}`
    );
  }

  getUserAdminRoles(entityId: string = '', userId: string = '') {
    return this.httpClient.get<IAssignedAdminRoles>(
      `${this.base}api/v${this.version}/${entityId}/Administrators/${userId}`
    );
  }

  setUserAdminRoles(
    entityId: string = '',
    userId: string = '',
    assignedRoles = []
  ) {
    return this.httpClient.put<IAssignedAdminRoles>(
      `${this.base}api/v${this.version}/${entityId}/Administrators/${userId}`,
      {
        roleIds: assignedRoles,
      }
    );
  }

  setUserRoles(
    entityId: string = '',
    userId: string = '',
    assignedRoles: {roleId: string, targetId?: string}[] = []
  ) {
    return this.httpClient.put<IAssignedAdminRoles>(
      `${this.base}api/v${this.version}/${entityId}/Administrators/${userId}/Batch`,
      {
        updates: assignedRoles,
      }
    );
  }

  createEntityRole(params: { request: ICreateOrUpdateEntityRoleRequest }) {
    return this.httpClient.post<IRole>(
      `${this.base}api/v${this.version}/${params.request.entityId}/Roles`,
      params.request
    );
  }

  updateEntityRole(params: {
    roleId: string;
    request: ICreateOrUpdateEntityRoleRequest;
  }) {
    return this.httpClient.put<IRole>(
      `${this.base}api/v${this.version}/${params.request.entityId}/Roles/${params.roleId}`,
      params.request
    );
  }
}
